/* eslint-disable no-nested-ternary */
import { useMemo, useState, useCallback } from 'react';
import { capitalCase } from 'change-case';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetTopic4ContentList,
  selectTopic4ContentPageNumber,
  selectGetTopic4ContentState,
  selectTopic4HasMoreContent,
  selectTopic4ContentList,
  UpdateTopic4HasMoreContent,
  UpdateTopic4PageNumber,
} from '../../../store/slices/contentSlice';
import { getTopic4Content } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface Topic4ContentCategoryProps {
  categoryId: string;
  topicName: string;
  viewMorePage?: boolean
  setSomeContentLoadedInitially: (value: boolean) => void
  contentFeedSection: ContentFeedSection
}

export function Topic4ContentCategory({
  categoryId,
  topicName,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:Topic4ContentCategoryProps) {
  const storeTopic4ContentList = useAppSelector(selectTopic4ContentList);
  const topic4ContentList = useMemo(() => storeTopic4ContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeTopic4ContentList, contentFeedSection]);

  const getTopic4ContentState = useAppSelector(selectGetTopic4ContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const topic4Page = useAppSelector(selectTopic4ContentPageNumber);
  const topic4HasMore = useAppSelector(selectTopic4HasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: topic4ContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { contentTopics: topicName },
    getCategoryContent: getTopic4Content,
    resetCategoryContentList: ResetTopic4ContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateTopic4PageNumber,
    updateHasMoreCategoryContent: UpdateTopic4HasMoreContent,
  });

  const isLoading = useMemo(
    () => (getTopic4ContentState === 'pending' && !initialFetchDone),
    [getTopic4ContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      topic4Page,
      topic4HasMore,
    );
  }, [topic4Page, topic4HasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${topicName}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${categoryId}-content`}
          categoryId={categoryId}
          storeContentList={topic4ContentList}
          onNextPage={onNextPage}
          hasMoreContent={topic4HasMore}
          showSearchBar={true}
        />
      ) : topic4ContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${topicName}-content`}
          categoryTitle={capitalCase(topicName)}
          storeContentList={topic4ContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
